import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router'
import { AuthProvider } from '@/lib/auth'
import { PlatformAuthProvider } from '@/contexts/PlatformAuthContext'
import { PlatformUsagePage } from '@/pages/PlatformUsagePage'
import { ThemeProvider } from '@/lib/theme'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { MfaRequiredGuard } from '@/components/MfaRequiredGuard'
import { AppLayout } from '@/components/layout/AppLayout'
// Eager (not lazy) so the OAuth popup completes instantly with no extra chunk fetch.
import { OAuthComplete } from '@/pages/OAuthComplete'
// Eager so the DSAR email-link confirmation renders instantly even when a stale SW serves the app-shell.
import { DsarComplete } from '@/pages/DsarComplete'

// Lazy-loaded pages for code splitting
const Login = lazy(() => import('@/pages/Login').then(m => ({ default: m.Login })))
const SignUp = lazy(() => import('@/pages/SignUp').then(m => ({ default: m.SignUp })))
const Onboarding = lazy(() => import('@/pages/Onboarding').then(m => ({ default: m.Onboarding })))
const AcceptInvite = lazy(() => import('@/pages/AcceptInvite').then(m => ({ default: m.AcceptInvite })))
const CommandCenter = lazy(() => import('@/pages/CommandCenter').then(m => ({ default: m.CommandCenter })))
const Contacts = lazy(() => import('@/pages/Contacts').then(m => ({ default: m.Contacts })))
const ContactDetail = lazy(() => import('@/pages/ContactDetail').then(m => ({ default: m.ContactDetail })))
const LeadOffers = lazy(() => import('@/pages/LeadOffers').then(m => ({ default: m.LeadOffers })))
const Calls = lazy(() => import('@/pages/Calls').then(m => ({ default: m.Calls })))
const Tasks = lazy(() => import('@/pages/Tasks').then(m => ({ default: m.Tasks })))
const Transactions = lazy(() => import('@/pages/Transactions').then(m => ({ default: m.Transactions })))
const TransactionDetail = lazy(() => import('@/pages/TransactionDetail').then(m => ({ default: m.TransactionDetail })))
const Properties = lazy(() => import('@/pages/Properties').then(m => ({ default: m.Properties })))
const PropertyDetail = lazy(() => import('@/pages/PropertyDetail').then(m => ({ default: m.PropertyDetail })))
const Cma = lazy(() => import('@/pages/Cma').then(m => ({ default: m.Cma })))
const Documents = lazy(() => import('@/pages/Documents').then(m => ({ default: m.Documents })))
const FormsLibrary = lazy(() => import('@/pages/FormsLibrary').then(m => ({ default: m.FormsLibrary })))
const CCRLibrary = lazy(() => import('@/pages/CCRLibrary').then(m => ({ default: m.CCRLibrary })))
const FollowUp = lazy(() => import('@/pages/FollowUp').then(m => ({ default: m.FollowUp })))
const Analytics = lazy(() => import('@/pages/Analytics').then(m => ({ default: m.Analytics })))
const SettingsPage = lazy(() => import('@/pages/SettingsPage').then(m => ({ default: m.SettingsPage })))
const Inbox = lazy(() => import('@/pages/Inbox').then(m => ({ default: m.Inbox })))
const Texting = lazy(() => import('@/pages/Texting').then(m => ({ default: m.Texting })))
const ReviewQueue = lazy(() => import('@/pages/ReviewQueue').then(m => ({ default: m.ReviewQueue })))
const Calendar = lazy(() => import('@/pages/Calendar').then(m => ({ default: m.Calendar })))
const Pricing = lazy(() => import('@/pages/Pricing').then(m => ({ default: m.Pricing })))
const FSBOListings = lazy(() => import('@/pages/FSBOListings').then(m => ({ default: m.FSBOListings })))
const FSBODetail = lazy(() => import('@/pages/FSBODetail').then(m => ({ default: m.FSBODetail })))
const ContactDedup = lazy(() => import('@/pages/ContactDedup').then(m => ({ default: m.ContactDedup })))
const Training = lazy(() => import('@/pages/Training').then(m => ({ default: m.Training })))
const TrainingOnboarding = lazy(() => import('@/pages/TrainingOnboarding').then(m => ({ default: m.TrainingOnboarding })))
const Commissions = lazy(() => import('@/pages/Commissions').then(m => ({ default: m.Commissions })))
const Referrals = lazy(() => import('@/pages/Referrals').then(m => ({ default: m.Referrals })))
const Vendors = lazy(() => import('@/pages/Vendors').then(m => ({ default: m.Vendors })))
const Deadlines = lazy(() => import('@/pages/Deadlines').then(m => ({ default: m.Deadlines })))
const ActionPlans = lazy(() => import('@/pages/ActionPlans').then(m => ({ default: m.ActionPlans })))
const Templates = lazy(() => import('@/pages/Templates').then(m => ({ default: m.Templates })))
const Showings = lazy(() => import('@/pages/Showings').then(m => ({ default: m.Showings })))
const Recordings = lazy(() => import('@/pages/Recordings').then(m => ({ default: m.Recordings })))
const DialogCheck = lazy(() => import('@/pages/DialogCheck').then(m => ({ default: m.DialogCheck })))
const DialogCheckBaseline = lazy(() => import('@/pages/DialogCheckBaseline').then(m => ({ default: m.DialogCheckBaseline })))
const MfaChallenge = lazy(() => import('@/pages/MfaChallenge').then(m => ({ default: m.MfaChallenge })))
const MfaEnroll = lazy(() => import('@/pages/MfaEnroll').then(m => ({ default: m.MfaEnroll })))
// A2P 10DLC + CCPA public pages — NO auth required
const PrivacyPolicy = lazy(() => import('@/pages/legal/PrivacyPolicy').then(m => ({ default: m.PrivacyPolicy })))
const TermsOfService = lazy(() => import('@/pages/legal/TermsOfService').then(m => ({ default: m.TermsOfService })))
const DoNotSell = lazy(() => import('@/pages/legal/DoNotSell').then(m => ({ default: m.DoNotSell })))
const SmsOptIn = lazy(() => import('@/pages/SmsOptIn').then(m => ({ default: m.SmsOptIn })))
const Dsar = lazy(() => import('@/pages/Dsar').then(m => ({ default: m.Dsar })))
const NotFound = lazy(() => import('@/pages/NotFound').then(m => ({ default: m.NotFound })))

function PageLoader() {
  return (
    <div className="flex h-full items-center justify-center">
      <div className="text-center">
        <span className="text-xl font-bold tracking-[-0.04em]">
          rize<span className="text-primary">.</span>
        </span>
      </div>
    </div>
  )
}

export default function App() {
  return (
    <ThemeProvider>
    <BrowserRouter>
      {window.location.pathname.replace(/\/$/, '') === '/platform/usage' ? <PlatformAuthProvider><PlatformUsagePage /></PlatformAuthProvider> : <AuthProvider>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public routes */}
            {import.meta.env.DEV && (
              <Route path="/_dialog-check" element={<DialogCheck />} />
            )}
            {import.meta.env.DEV && (
              <Route path="/_dialog-check-baseline" element={<DialogCheckBaseline />} />
            )}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/onboarding" element={<Onboarding />} />
            <Route path="/invite/:token" element={<AcceptInvite />} />
            <Route path="/mfa/challenge" element={<MfaChallenge />} />
            {/* Mandatory-MFA enrollment. MUST stay outside MfaRequiredGuard: the
                guard's need-enrollment arm redirects here, and its old target
                (/settings?tab=security) was itself guard-wrapped — the guard
                re-fired on arrival and every content page rendered blank. */}
            <Route path="/mfa/enroll" element={<MfaEnroll />} />

            {/* A2P 10DLC + CCPA public pages — outside auth gate, no ProtectedRoute */}
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            {/* CCPA/CPRA Do-Not-Sell-or-Share notice + GPC honored confirmation, public, no auth */}
            <Route path="/do-not-sell" element={<DoNotSell />} />
            <Route path="/opt-in" element={<SmsOptIn />} />
            {/* DSAR intake form — CCPA/CPRA, public, no auth */}
            <Route path="/dsar" element={<Dsar />} />

            {/* OAuth popup completion — SPA fallback for when a service worker serves
                the app-shell for /oauth-complete.html instead of the static page
                (e.g. a stale pre-denylist worker). Public, no auth — it only
                postMessages the opener and closes. */}
            <Route path="/oauth-complete.html" element={<OAuthComplete />} />
            <Route path="/oauth-complete" element={<OAuthComplete />} />

            {/* DSAR verification confirmation — SPA fallback for when a service
                worker serves the app-shell for /dsar-complete.html instead of the
                static page (e.g. a stale pre-denylist worker). Public, no auth —
                dsar-verify (edge fn) 302-redirects here after flipping the DB. */}
            <Route path="/dsar-complete.html" element={<DsarComplete />} />
            <Route path="/dsar-complete" element={<DsarComplete />} />

            {/* Training onboarding — requires auth but no training completion check */}
            <Route element={<ProtectedRoute skipTrainingGate />}>
              <Route path="/onboarding/training" element={<TrainingOnboarding />} />
            </Route>

            {/* Protected routes — training gate enforced in ProtectedRoute */}
            <Route element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }>
              <Route path="/" element={<MfaRequiredGuard><CommandCenter /></MfaRequiredGuard>} />
              <Route path="/contacts" element={<MfaRequiredGuard><Contacts /></MfaRequiredGuard>} />
              {/* RIZ127: leads a First to Claim rule offered to this member. A static segment, so it
                  ranks above /contacts/:id (as /contacts/dedup does). */}
              <Route path="/contacts/claim" element={<MfaRequiredGuard><LeadOffers /></MfaRequiredGuard>} />
              <Route path="/contacts/:id" element={<MfaRequiredGuard><ContactDetail /></MfaRequiredGuard>} />
              <Route path="/calls" element={<MfaRequiredGuard><Calls /></MfaRequiredGuard>} />
              {/* The Plaud triage queue. MfaRequiredGuard is not decoration here: the table
                  carries the RESTRICTIVE mfa_ok() overlay (mig 236), so an MFA-enrolled user on
                  an AAL1 token reads zero rows — the page would render honestly empty rather
                  than error, which is the worst of both. */}
              <Route path="/recordings" element={<MfaRequiredGuard><Recordings /></MfaRequiredGuard>} />
              <Route path="/tasks" element={<MfaRequiredGuard><Tasks /></MfaRequiredGuard>} />
              <Route path="/transactions" element={<MfaRequiredGuard><Transactions /></MfaRequiredGuard>} />
              {/* RIZ051: the brokerage's Action Plans as a page under Transactions. Declared
                  before the :id route so the static segment is unmistakable to a reader
                  (the router ranks it higher regardless of order). */}
              <Route path="/transactions/plans" element={<MfaRequiredGuard><ActionPlans /></MfaRequiredGuard>} />
              <Route path="/transactions/:id" element={<MfaRequiredGuard><TransactionDetail /></MfaRequiredGuard>} />
              <Route path="/deadlines" element={<MfaRequiredGuard><Deadlines /></MfaRequiredGuard>} />
              {/* N4/D9: showings across every listing, and what the showing agents said. */}
              <Route path="/showings" element={<MfaRequiredGuard><Showings /></MfaRequiredGuard>} />
              <Route path="/properties" element={<MfaRequiredGuard><Properties /></MfaRequiredGuard>} />
              <Route path="/properties/:id" element={<MfaRequiredGuard><PropertyDetail /></MfaRequiredGuard>} />
              {/* Row 61: the CMA had no route at all — it existed only as a modal inside Listings. */}
              <Route path="/cma" element={<MfaRequiredGuard><Cma /></MfaRequiredGuard>} />
              <Route path="/ccr-library" element={<MfaRequiredGuard><CCRLibrary /></MfaRequiredGuard>} />
              <Route path="/documents" element={<MfaRequiredGuard><Documents /></MfaRequiredGuard>} />
              <Route path="/forms" element={<MfaRequiredGuard><FormsLibrary /></MfaRequiredGuard>} />
              <Route path="/follow-up" element={<MfaRequiredGuard><FollowUp /></MfaRequiredGuard>} />
              {/* R4-4: her-words alias — /follow-up stays canonical (deep links). */}
              <Route path="/campaigns" element={<MfaRequiredGuard><FollowUp /></MfaRequiredGuard>} />
              {/* RIZ047: the brokerage's reusable one-off email and text templates. */}
              <Route path="/templates" element={<MfaRequiredGuard><Templates /></MfaRequiredGuard>} />
              <Route path="/inbox" element={<MfaRequiredGuard><Inbox /></MfaRequiredGuard>} />
              {/* Texting inbox — /texting/:contactId is the conversation deep
                  link the inbound-text bell notification navigates to.
                  /texting/g/:conversationId is the GROUP thread (RIZ040): a
                  separate segment because the two ids come from different
                  tables, so the pane must know which query to run before it can
                  render. Declared FIRST — React Router ranks the static "g"
                  segment above the :contactId param, but the order also says
                  which one is meant to win. */}
              <Route path="/texting" element={<MfaRequiredGuard><Texting /></MfaRequiredGuard>} />
              <Route path="/texting/g/:conversationId" element={<MfaRequiredGuard><Texting /></MfaRequiredGuard>} />
              <Route path="/texting/:contactId" element={<MfaRequiredGuard><Texting /></MfaRequiredGuard>} />
              <Route path="/review" element={<MfaRequiredGuard><ReviewQueue /></MfaRequiredGuard>} />
              <Route path="/calendar" element={<MfaRequiredGuard><Calendar /></MfaRequiredGuard>} />
              <Route path="/analytics" element={<MfaRequiredGuard><Analytics /></MfaRequiredGuard>} />
              <Route path="/pricing" element={<MfaRequiredGuard><Pricing /></MfaRequiredGuard>} />
              {/* Profile lives under Settings > Preferences (operator decision
                  2026-07-08); old bookmarks/deep-links land there. */}
              <Route path="/profile" element={<Navigate to="/settings?tab=preferences" replace />} />
              <Route path="/fsbo" element={<MfaRequiredGuard><FSBOListings /></MfaRequiredGuard>} />
              <Route path="/fsbo/:id" element={<MfaRequiredGuard><FSBODetail /></MfaRequiredGuard>} />
              <Route path="/contacts/dedup" element={<MfaRequiredGuard><ContactDedup /></MfaRequiredGuard>} />
              <Route path="/training" element={<MfaRequiredGuard><Training /></MfaRequiredGuard>} />
              <Route path="/commissions" element={<MfaRequiredGuard><Commissions /></MfaRequiredGuard>} />
              <Route path="/referrals" element={<MfaRequiredGuard><Referrals /></MfaRequiredGuard>} />
              <Route path="/vendors" element={<MfaRequiredGuard><Vendors /></MfaRequiredGuard>} />
              <Route path="/settings" element={<MfaRequiredGuard><SettingsPage /></MfaRequiredGuard>} />
            </Route>

            {/* Catch-all — unknown URLs previously rendered a blank page. */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </AuthProvider>}
    </BrowserRouter>
    </ThemeProvider>
  )
}
